import { MotionConfig } from "motion/react";
import { lazy } from "react";
import { Toaster } from "react-hot-toast";
import { Route, Routes } from "react-router-dom";

import { LazyBoundary } from "@/components/common";
import { ROUTES } from "@/constants/routes";
import { useQuizTheme } from "@/hooks/useQuizTheme";
import BaseLayout from "@/layouts/BaseLayout";
import StandaloneLayout from "@/layouts/StandaloneLayout";
import ProtectedRoute from "@/providers/ProtectedRoute";
import SubscribedRoute from "@/providers/SubscribedRoute";
import { ThemeProvider } from "@/providers/ThemeProvider";
import Avatars from "@/views/Avatars";
import CreateView from "@/views/CreateView";
import { Landing } from "@/views/Landing";
import Login from "@/views/Login";
import MagicLogin from "@/views/MagicLogin";
import ProfileView from "@/views/ProfileView";
import SettingsView from "@/views/SettingsView";
import SignUp from "@/views/SignUp";
import SubscriptionView from "@/views/SubscriptionView";
import { Videos } from "@/views/Videos";

const QuizPage = lazy(() => import("@/views/QuizPage"));
const QuizOnboardingPage = lazy(() =>
  import("@/views/QuizOnboarding").then((module) => ({
    default: module.QuizOnboarding,
  })),
);

const PrivacyPolicy = lazy(() => import("@/views/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/views/TermsOfService"));

const PaywallView = lazy(() =>
  import("@/views/Paywall").then((module) => ({ default: module.PaywallView })),
);

export default function App() {
  const quizTheme = useQuizTheme();

  return (
    <MotionConfig reducedMotion="user">
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            borderRadius: "12px",
            background: "#18181B",
            color: "#fff",
            fontSize: "14px",
            padding: "12px 20px",
          },
          success: {
            iconTheme: { primary: "#7C3AED", secondary: "#fff" },
          },
        }}
      />
      <Routes>
        <Route path={ROUTES.MAGIC_LINK} element={<MagicLogin />} />
        <Route element={<BaseLayout />}>
          <Route path={ROUTES.HOME} element={<Landing />} />

          <Route path={ROUTES.LOGIN} element={<Login />} />
          <Route path={ROUTES.SIGNUP} element={<SignUp />} />
          <Route
            path={ROUTES.PRIVACY}
            element={
              <LazyBoundary>
                <PrivacyPolicy />
              </LazyBoundary>
            }
          />
          <Route
            path={ROUTES.TERMS}
            element={
              <LazyBoundary>
                <TermsOfService />
              </LazyBoundary>
            }
          />
          <Route
            path={ROUTES.CREATE}
            element={
              <ProtectedRoute>
                <SubscribedRoute>
                  <CreateView />
                </SubscribedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.VIDEOS}
            element={
              <ProtectedRoute>
                <SubscribedRoute>
                  <Videos />
                </SubscribedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.AVATARS}
            element={
              <ProtectedRoute>
                <SubscribedRoute>
                  <Avatars />
                </SubscribedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.PROFILE}
            element={
              <ProtectedRoute>
                <ProfileView />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.PROFILE_SETTINGS}
            element={
              <ProtectedRoute>
                <SettingsView />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.PROFILE_SUBSCRIPTION}
            element={
              <ProtectedRoute>
                <SubscriptionView />
              </ProtectedRoute>
            }
          />
        </Route>

        <Route element={<StandaloneLayout />}>
          <Route
            path={ROUTES.PAYWALL(":paywallId")}
            element={
              <LazyBoundary>
                <ProtectedRoute>
                  <ThemeProvider theme={quizTheme}>
                    <PaywallView />
                  </ThemeProvider>
                </ProtectedRoute>
              </LazyBoundary>
            }
          />
          <Route
            path={ROUTES.QUIZ(":quizId")}
            element={
              <LazyBoundary>
                <ThemeProvider theme={quizTheme}>
                  <QuizPage />
                </ThemeProvider>
              </LazyBoundary>
            }
          />
          <Route
            path={ROUTES.ONBOARDING(":variant")}
            element={
              <LazyBoundary>
                <ProtectedRoute>
                  <ThemeProvider theme={quizTheme}>
                    <QuizOnboardingPage />
                  </ThemeProvider>
                </ProtectedRoute>
              </LazyBoundary>
            }
          />
        </Route>
      </Routes>
    </MotionConfig>
  );
}
